import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Field, MessageBar, MessageBarBody, Select, Spinner, Text, makeStyles, tokens } from '@fluentui/react-components';
import { useExtractDr15FromUpload, useUploadDocument, useUploads } from '../services/conversionsApi';
import type { DocumentCategory, Dr15ExtractionResult, UploadedDocument } from '../types';
import { UploadControl } from './upload/UploadControl';
import { useUploadQueue, type UploadRules } from './upload/useUploadQueue';
import PayrollReportGuide from './PayrollReportGuide';

const useStyles = makeStyles({
  box: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  row: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'end', flexWrap: 'wrap' },
  muted: { color: tokens.colorNeutralForeground3 },
});

/** Each journal source's words: `journalPage.<prefix>.*`, the same sub-keys for every kind. */
const KEYS: Partial<Record<DocumentCategory, string>> = {
  dr15Return: 'dr15Source',
  posReport: 'posSource',
  payrollReport: 'payrollSource',
};

/**
 * C87's rules per source. A DR-15 is READ, one return at a time, and the server takes only a PDF. A POS or payroll report
 * is attached, and a period can have several (a payroll journal and its tax liability report), each its own upload.
 */
const RULES: Partial<Record<DocumentCategory, UploadRules>> = {
  dr15Return: { accept: ['application/pdf'], maxBytes: 25 * 1024 * 1024, maxFiles: 1 },
};
const REPORT_RULES: UploadRules = { accept: ['*'], maxBytes: 25 * 1024 * 1024, maxFiles: 5 };

type Props = { clientId: string } & (
  | { category: 'dr15Return'; onRead: (doc: UploadedDocument, result: Dr15ExtractionResult) => void }
  | { category: 'posReport' | 'payrollReport'; onAttach: (doc: UploadedDocument) => void }
);

/**
 * A journal's SOURCE, from inside the journal. Two walks put it here:
 *
 * - 2026-10-28, deploy #19's walk: the DR-15 upload had been gone since `ca8a234` (#14, 2026-08-04), when retiring
 *   `/export` moved its upload card into `UploadsPanel`, mounted on the bank screens and the Documents tab but never
 *   here. A DR-15 is uploaded, or picked, and READ (the same extraction as everywhere else); `onRead` puts the B1
 *   handoff params on the URL so the existing prefill fills the figures.
 * - 2026-10-28, deploy #20's walk: the bank screens offer statements only (`lib/documentKinds`), so a POS report and a
 *   payroll report are uploaded here too. They are ATTACHED, not read: `onAttach` links the report to the journal,
 *   which cites it at export. Reading a POS report's figures is the shared engine's (PtcCore.Extraction P2), consumed
 *   when core publishes it, never built here.
 *
 * Nothing here writes a journal.
 */
export default function JournalSourcePicker(props: Props) {
  const { clientId, category } = props;
  const styles = useStyles();
  const { t } = useTranslation();
  const uploads = useUploads(clientId);
  const upload = useUploadDocument();
  const extract = useExtractDr15FromUpload();
  const queue = useUploadQueue(RULES[category] ?? REPORT_RULES);
  const [sending, setSending] = useState(false);
  const [picked, setPicked] = useState('');
  const [error, setError] = useState<string | null>(null);
  const k = (key: string) => t(`journalPage.${KEYS[category]}.${key}`);

  const sources = (uploads.data ?? [])
    .filter((u) => u.category === category && !u.isDeleted)
    .sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt));
  const busy = sending || extract.isPending;
  const failed = (err: unknown) => setError(err instanceof Error ? err.message : k('failed'));

  const use = (doc: UploadedDocument) => {
    if (props.category === 'dr15Return') {
      const { onRead } = props;
      extract.mutate({ clientId, documentId: doc.id }, { onSuccess: (result) => onRead(doc, result), onError: failed });
    } else {
      props.onAttach(doc);
    }
  };

  /** C87: each queued file is its own upload, with its caption; each one stored is then used (read, or attached). */
  const uploadAndUse = async () => {
    if (queue.sendableCount === 0) return;
    setError(null);
    setSending(true);
    try {
      const result = await queue.upload(async (file, caption, onProgress) => {
        try {
          const doc = await upload.mutateAsync({ clientId, category, file, caption });
          onProgress(100);
          use(doc);
        } catch (err) {
          failed(err);
          throw err;
        }
      });
      if (result.failed === 0) queue.clear();
    } finally {
      setSending(false);
    }
  };

  const usePicked = () => {
    const doc = sources.find((u) => u.id === picked);
    if (!doc) return;
    setError(null);
    use(doc);
  };

  return (
    <div className={styles.box}>
      <Text weight="semibold">{k('heading')}</Text>
      <Text className={styles.muted}>{k('hint')}</Text>
      {/* Wave 5 §2.3: which report to bring, per provider, and which are the wrong ones. */}
      {category === 'payrollReport' && <PayrollReportGuide />}
      <div className={styles.box} aria-label={k('file')} role="group">
        <UploadControl queue={queue} disabled={busy} />
        <div className={styles.row}>
          <Button appearance="primary" onClick={() => void uploadAndUse()} disabled={queue.sendableCount === 0 || busy}>
            {k('upload')}
          </Button>
        </div>
      </div>
      {sources.length > 0 && (
        <div className={styles.row}>
          <Field label={k('pickLabel')}>
            <Select value={picked} onChange={(_e, data) => setPicked(data.value)}>
              <option value="">{k('pickNone')}</option>
              {sources.map((u) => (
                <option key={u.id} value={u.id}>{`${u.fileName} · ${u.uploadedAt.slice(0, 10)}`}</option>
              ))}
            </Select>
          </Field>
          <Button onClick={usePicked} disabled={!picked || busy}>{k('read')}</Button>
        </div>
      )}
      {busy && <Spinner size="tiny" label={sending ? k('uploading') : t('journalPage.dr15Source.reading')} />}
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
}
